import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { getAdminSecurityConfig, setAdminRole } from '../../services/admin/rolesService';

const ROLE_OPTIONS = [
  { value: 'super_admin', labelKey: 'admin.settings.role.super_admin' },
  { value: 'admin', labelKey: 'admin.settings.role.admin' },
  { value: 'support', labelKey: 'admin.settings.role.support' },
  { value: '', labelKey: 'admin.settings.role.remove' },
];

export default function AdminSettingsPage() {
  const { t } = useLanguage();
  const { refreshAdminRole, currentUser } = useAuth();
  const [index, setIndex] = useState(null);
  const [targetUid, setTargetUid] = useState('');
  const [role, setRole] = useState('admin');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const data = await getAdminSecurityConfig();
      setIndex(data?.roleIndex || null);
    } catch (e) {
      setError(e?.message || 'Error');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    setMessage('');
    const operationId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `role-${Date.now()}`;
    try {
      const result = await setAdminRole({
        targetUid: targetUid.trim(),
        role: role || null,
        operationId,
      });
      setMessage(t('admin.settings.saved'));
      if (targetUid.trim() === currentUser?.uid) {
        await refreshAdminRole?.();
        window.location.reload();
        return;
      }
      await load();
      if (result?.appliesAt === 'next_token_refresh') {
        setMessage(t('admin.settings.savedRefresh'));
      }
    } catch (err) {
      setError(err?.message || 'Error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <h1 className="text-xl font-semibold">{t('admin.settings.title')}</h1>
      <p className="text-sm text-slate-600">{t('admin.settings.help')}</p>
      {index ? (
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm">
          <p className="font-medium">{t('admin.settings.indexTitle')}</p>
          {['super_admin', 'admin', 'support'].map((key) => (
            <p key={key} className="mt-2 break-all text-slate-700">
              <span className="font-semibold">{key}:</span>{' '}
              {(index[key] || []).join(', ') || '—'}
            </p>
          ))}
        </div>
      ) : null}
      <form onSubmit={handleSubmit} className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
        <label className="block text-sm">
          <span className="text-slate-600">{t('admin.settings.targetUid')}</span>
          <input
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
            value={targetUid}
            onChange={(e) => setTargetUid(e.target.value)}
            required
          />
        </label>
        <label className="block text-sm">
          <span className="text-slate-600">{t('admin.settings.role')}</span>
          <select
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            {ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </option>
            ))}
          </select>
        </label>
        {error ? <p className="text-sm text-red-600">{error}</p> : null}
        {message ? <p className="text-sm text-emerald-700">{message}</p> : null}
        <button
          type="submit"
          disabled={busy || !targetUid.trim()}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
        >
          {busy ? '…' : t('admin.settings.submit')}
        </button>
      </form>
    </div>
  );
}
